import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  BookOpen, Heart, ChevronRight, 
  Sun, RotateCw, Search, ArrowLeft, 
  X, Play, Pause, 
  Moon, CheckCircle2, 
  Compass, Book, Home, Trash2,
  RotateCcw, ScrollText, Share2,
  Bell, BellOff, Volume2, Edit, Plus, MapPin
} from 'lucide-react';

// --- DATA ---

const PRESET_TESBIHAT = [
  { id: 1, label: "Sübhanallah", target: 33, meaning: "Allah noksan sıfatlardan münezzehtir" },
  { id: 2, label: "Elhamdülillah", target: 33, meaning: "Hamd Allah'a mahsustur" },
  { id: 3, label: "Allahu Ekber", target: 33, meaning: "Allah en büyüktür" },
  { id: 4, label: "Lâ ilâhe illallah", target: 100, meaning: "Allah'tan başka ilah yoktur" },
  { id: 5, label: "Estağfirullah", target: 100, meaning: "Allah'tan bağışlanma dilerim" },
  { id: 6, label: "Salavat-ı Şerife", target: 100, meaning: "Hz. Muhammed'e salat ve selam olsun" },
  { id: 7, label: "Hasbünallah", target: 100, meaning: "Allah bize yeter" },
  { id: 8, label: "Ya Fettah", target: 489, meaning: "Ey her türlü kapıları açan" },
  { id: 9, label: "Ya Vedûd", target: 400, meaning: "Ey çok seven ve sevilen" }
];

const HADITH_LIBRARY = [
  { 
    id: 'buhari', title: "Sahih-i Buhârî", author: "İmam Buhârî", 
    color: "#dbeafe", textColor: "#1d4ed8",
    hadiths: [
       { id: "b1", type: 'hadith', text: "Ameller niyetlere göredir.", topic: "Niyet" },
       { id: "b2", type: 'hadith', text: "Müslüman, elinden ve dilinden diğer Müslümanların emin olduğu kimsedir.", topic: "Güven" },
       { id: "b3", type: 'hadith', text: "Kolaylaştırınız, zorlaştırmayınız; müjdeleyiniz, nefret ettirmeyiniz.", topic: "Tebliğ" }
    ]
  },
  { 
    id: 'muslim', title: "Sahih-i Müslim", author: "İmam Müslim", 
    color: "#d1fae5", textColor: "#047857",
    hadiths: [
       { id: "m1", type: 'hadith', text: "Temizlik imanın yarısıdır.", topic: "Temizlik" },
       { id: "m2", type: 'hadith', text: "Kulun Rabbine en yakın olduğu an secde anıdır.", topic: "Namaz" }
    ]
  }
];

const SURAH_NAMES_TR = [
  "Fâtiha", "Bakara", "Âl-i İmrân", "Nisâ", "Mâide", "En'âm", "A'râf", "Enfâl", "Tevbe", "Yûnus",
  "Hûd", "Yûsuf", "Ra'd", "İbrâhîm", "Hicr", "Nahl", "İsrâ", "Kehf", "Meryem", "Tâhâ", "Enbiyâ", "Hac",
  "Mü'minûn", "Nûr", "Furkân", "Şu'arâ", "Neml", "Kasas", "Ankebût", "Rûm", "Lokmân", "Secde", "Ahzâb",
  "Sebe'", "Fâtır", "Yâsîn", "Sâffât", "Sâd", "Zümer", "Mü'min", "Fussilet", "Şûrâ", "Zuhruf", "Duhân",
  "Câsiye", "Ahkâf", "Muhammed", "Fetih", "Hucurât", "Kâf", "Zâriyât", "Tûr", "Necm", "Kamer", "Rahmân",
  "Vâkıa", "Hadîd", "Mücâdele", "Haşr", "Mümtehine", "Saf", "Cuma", "Münâfikûn", "Teğâbûn", "Talâk",
  "Tahrîm", "Mülk", "Kalem", "Hâkka", "Meâric", "Nûh", "Cin", "Müzzemmil", "Müddessir", "Kıyâme", "İnsân",
  "Mürselât", "Nebe'", "Nâziât", "Abese", "Tekvîr", "İnfitâr", "Mutaffifîn", "İnşikâk", "Burûc", "Târık",
  "A'lâ", "Gâşiye", "Fecr", "Beled", "Şems", "Leyl", "Duhâ", "İnşirah", "Tîn", "Alak", "Kadir", "Beyyine",
  "Zilzâl", "Âdiyât", "Kâria", "Tekâsür", "Asr", "Hümeze", "Fîl", "Kureyş", "Mâûn", "Kevser", "Kâfirûn",
  "Nasr", "Tebbet", "İhlâs", "Felak", "Nâs"
];

const CITIES = [
  { name: "Adana", lat: 37.0000, lng: 35.3213 }, { name: "Adıyaman", lat: 37.7648, lng: 38.2786 }, { name: "Afyonkarahisar", lat: 38.7507, lng: 30.5567 },
  { name: "Ağrı", lat: 39.7191, lng: 43.0503 }, { name: "Aksaray", lat: 38.3687, lng: 34.0370 }, { name: "Amasya", lat: 40.6501, lng: 35.8360 },
  { name: "Ankara", lat: 39.9334, lng: 32.8597 }, { name: "Antalya", lat: 36.8969, lng: 30.7133 }, { name: "Ardahan", lat: 41.1105, lng: 42.7022 },
  { name: "Artvin", lat: 41.1828, lng: 41.8183 }, { name: "Aydın", lat: 37.8560, lng: 27.8416 }, { name: "Balıkesir", lat: 39.6484, lng: 27.8826 },
  { name: "Bartın", lat: 41.6344, lng: 32.3375 }, { name: "Batman", lat: 37.8812, lng: 41.1291 }, { name: "Bayburt", lat: 40.2552, lng: 40.2249 },
  { name: "Bilecik", lat: 40.1451, lng: 29.9799 }, { name: "Bingöl", lat: 38.8854, lng: 40.4983 }, { name: "Bitlis", lat: 38.4006, lng: 42.1095 },
  { name: "Bolu", lat: 40.7350, lng: 31.6061 }, { name: "Burdur", lat: 37.7204, lng: 30.2908 }, { name: "Bursa", lat: 40.1885, lng: 29.0610 },
  { name: "Çanakkale", lat: 40.1553, lng: 26.4142 }, { name: "Çankırı", lat: 40.6013, lng: 33.6134 }, { name: "Çorum", lat: 40.5506, lng: 34.9556 },
  { name: "Denizli", lat: 37.7765, lng: 29.0864 }, { name: "Diyarbakır", lat: 37.9144, lng: 40.2306 }, { name: "Düzce", lat: 40.8438, lng: 31.1565 },
  { name: "Edirne", lat: 41.6768, lng: 26.5603 }, { name: "Elazığ", lat: 38.6810, lng: 39.2264 }, { name: "Erzincan", lat: 39.7500, lng: 39.5000 },
  { name: "Erzurum", lat: 39.9000, lng: 41.2700 }, { name: "Eskişehir", lat: 39.7767, lng: 30.5206 }, { name: "Gaziantep", lat: 37.0662, lng: 37.3833 },
  { name: "Giresun", lat: 40.9128, lng: 38.3895 }, { name: "Gümüşhane", lat: 40.4600, lng: 39.4700 }, { name: "Hakkari", lat: 37.5833, lng: 43.7333 },
  { name: "Hatay", lat: 36.4018, lng: 36.3498 }, { name: "Iğdır", lat: 39.9167, lng: 44.0333 }, { name: "Isparta", lat: 37.7648, lng: 30.5566 },
  { name: "İstanbul", lat: 41.0082, lng: 28.9784 }, { name: "İzmir", lat: 38.4192, lng: 27.1287 }, { name: "Kahramanmaraş", lat: 37.5858, lng: 36.9371 },
  { name: "Karabük", lat: 41.2061, lng: 32.6204 }, { name: "Karaman", lat: 37.1759, lng: 33.2287 }, { name: "Kars", lat: 40.6167, lng: 43.1000 },
  { name: "Kastamonu", lat: 41.3887, lng: 33.7827 }, { name: "Kayseri", lat: 38.7312, lng: 35.4787 }, { name: "Kilis", lat: 36.7184, lng: 37.1212 },
  { name: "Kırıkkale", lat: 39.8468, lng: 33.5153 }, { name: "Kırklareli", lat: 41.7333, lng: 27.2167 }, { name: "Kırşehir", lat: 39.1425, lng: 34.1709 },
  { name: "Kocaeli", lat: 40.8533, lng: 29.8815 }, { name: "Konya", lat: 37.8667, lng: 32.4833 }, { name: "Kütahya", lat: 39.4167, lng: 29.9833 },
  { name: "Malatya", lat: 38.3552, lng: 38.3095 }, { name: "Manisa", lat: 38.6191, lng: 27.4289 }, { name: "Mardin", lat: 37.3212, lng: 40.7245 },
  { name: "Mersin", lat: 36.8000, lng: 34.6333 }, { name: "Muğla", lat: 37.2153, lng: 28.3636 }, { name: "Muş", lat: 38.9462, lng: 41.7539 },
  { name: "Nevşehir", lat: 38.6939, lng: 34.6857 }, { name: "Niğde", lat: 37.9667, lng: 34.6833 }, { name: "Ordu", lat: 40.9839, lng: 37.8764 },
  { name: "Osmaniye", lat: 37.0742, lng: 36.2472 }, { name: "Rize", lat: 41.0201, lng: 40.5234 }, { name: "Sakarya", lat: 40.7569, lng: 30.3783 },
  { name: "Samsun", lat: 41.2928, lng: 36.3313 }, { name: "Siirt", lat: 37.9333, lng: 41.9500 }, { name: "Sinop", lat: 42.0231, lng: 35.1531 },
  { name: "Sivas", lat: 39.7477, lng: 37.0179 }, { name: "Şanlıurfa", lat: 37.1591, lng: 38.7969 }, { name: "Şırnak", lat: 37.5164, lng: 42.4611 },
  { name: "Tekirdağ", lat: 40.9833, lng: 27.5167 }, { name: "Tokat", lat: 40.3167, lng: 36.5500 }, { name: "Trabzon", lat: 41.0015, lng: 39.7178 },
  { name: "Tunceli", lat: 39.1079, lng: 39.5401 }, { name: "Uşak", lat: 38.6823, lng: 29.4082 }, { name: "Van", lat: 38.4891, lng: 43.4089 },
  { name: "Yalova", lat: 40.6500, lng: 29.2667 }, { name: "Yozgat", lat: 39.8181, lng: 34.8147 }, { name: "Zonguldak", lat: 41.4564, lng: 31.7987 }
];

// --- STYLES ---
const cssStyles = `
  :root {
    --primary: #059669;
    --primary-light: #d1fae5;
    --primary-dark: #047857;
    --bg-body: #f8fafc;
    --bg-card: #ffffff;
    --text-main: #1e293b;
    --text-muted: #64748b;
    --border: #e2e8f0;
    --danger: #ef4444;
    --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    --radius-lg: 1rem;
    --radius-full: 9999px;
    --font-serif: 'Amiri', serif;
  }

  .dark {
    --bg-body: #020617;
    --bg-card: #0f172a;
    --text-main: #f1f5f9;
    --text-muted: #cbd5e1;
    --border: #1e293b;
    --primary-light: rgba(5, 150, 105, 0.2);
  }

  * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  body { 
    margin: 0; 
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; 
    background-color: var(--bg-body); 
    color: var(--text-main); 
    transition: background-color 0.3s, color 0.3s;
  }

  /* GENEL METİN RENGİ ZORLAMASI (DARK MODE FIX) */
  h1, h2, h3, h4, h5, h6, p, span, div, button, input {
    color: inherit;
  }

  /* Layout */
  .app-container {
    padding-top: 4rem;
    padding-bottom: 6rem;
    max-width: 600px;
    margin: 0 auto;
    min-height: 100vh;
    padding-left: 1rem;
    padding-right: 1rem;
    color: var(--text-main);
  }

  /* Header */
  .header {
    position: fixed; top: 0; left: 0; right: 0;
    height: 4rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--border);
    display: flex; justify-content: space-between; align-items: center;
    padding: 0 1rem;
    z-index: 50;
    color: var(--text-main);
  }
  .dark .header { background: rgba(15, 23, 42, 0.9); }
  
  .header-logo {
    width: 2rem; height: 2rem;
    background: linear-gradient(135deg, #059669, #0d9488);
    color: white; border-radius: 0.5rem;
    display: flex; align-items: center; justify-content: center;
    font-weight: bold; font-family: serif;
  }

  /* Buttons */
  .btn-icon {
    padding: 0.5rem;
    border-radius: 9999px;
    border: none;
    background: transparent;
    color: var(--text-main);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s;
  }
  .btn-icon:hover { background-color: var(--border); }
  .btn-primary {
    background-color: var(--primary);
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: var(--radius-lg);
    font-weight: bold;
    border: none;
    cursor: pointer;
    width: 100%;
  }

  /* Cards */
  .card {
    background-color: var(--bg-card);
    color: var(--text-main);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    padding: 1rem;
    box-shadow: var(--shadow);
    margin-bottom: 1rem;
  }
  .vakit-card {
    background: linear-gradient(145deg, #0f172a, #1e293b);
    color: white;
    display: flex; justify-content: space-between; align-items: center;
    padding: 1.5rem;
    border-radius: 1.5rem;
  }

  /* Grid & Flex Utilities */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; }
  .flex-row { display: flex; align-items: center; gap: 0.5rem; }
  .flex-between { display: flex; justify-content: space-between; align-items: center; }
  .flex-center { display: flex; align-items: center; justify-content: center; }
  .flex-col { display: flex; flex-direction: column; }

  /* Typography */
  .text-xs { font-size: 0.75rem; }
  .text-sm { font-size: 0.875rem; }
  .text-lg { font-size: 1.125rem; }
  .text-xl { font-size: 1.25rem; }
  .text-2xl { font-size: 1.5rem; }
  .text-4xl { font-size: 2.25rem; }
  .font-bold { font-weight: 700; }
  .text-center { text-align: center; }
  .text-primary { color: var(--primary); }
  .text-muted { color: var(--text-muted); }
  .uppercase { text-transform: uppercase; }

  /* Tesbih Specific */
  .tesbih-scroll {
    display: flex; gap: 0.75rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    scroll-snap-type: x mandatory;
  }
  .tesbih-item {
    flex-shrink: 0;
    scroll-snap-align: center;
    padding: 0.5rem 1rem;
    border-radius: 0.75rem;
    border: 1px solid var(--border);
    background: var(--bg-card);
    color: var(--text-muted);
    font-weight: 500;
    cursor: pointer;
  }
  .tesbih-item.active {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
  
  .tesbih-ring-container {
    position: relative;
    width: 300px; height: 300px;
    margin: 0 auto;
  }
  .tesbih-btn {
    position: absolute; inset: 15%;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, var(--bg-card), var(--border));
    border: 4px solid var(--border);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    box-shadow: inset 0 2px 10px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.1);
    cursor: pointer;
    transition: transform 0.1s;
    color: var(--text-main);
  }
  .tesbih-btn:active { transform: scale(0.95); }

  /* Modal */
  .modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    z-index: 100;
    padding: 1rem;
  }
  .modal-content {
    background: var(--bg-card);
    color: var(--text-main);
    border-radius: 1.5rem;
    padding: 1.5rem;
    width: 100%; max-width: 320px;
    box-shadow: var(--shadow-lg);
    max-height: 80vh;
    display: flex; flex-direction: column;
    border: 1px solid var(--border);
  }
  .input-field {
    width: 100%; padding: 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--bg-body);
    color: var(--text-main);
    margin-bottom: 1rem;
    font-size: 1rem;
  }

  /* Bottom Nav */
  .bottom-nav {
    position: fixed; bottom: 0; left: 0; right: 0;
    height: 4.5rem;
    background: var(--bg-card);
    border-top: 1px solid var(--border);
    display: flex; justify-content: space-around; align-items: center;
    z-index: 40;
  }
  .nav-item {
    display: flex; flex-direction: column; align-items: center; gap: 0.25rem;
    background: none; border: none;
    color: var(--text-muted);
    font-size: 0.7rem; font-weight: 600;
    cursor: pointer;
  }
  .nav-item.active { color: var(--primary); }

  /* Animations */
  @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
  .animate-pulse { animation: pulse 2s infinite; }
  .toast {
    position: fixed; top: 5rem; left: 50%; transform: translateX(-50%);
    background: #1e293b; color: white;
    padding: 0.75rem 1.5rem; border-radius: 99px;
    font-size: 0.875rem; z-index: 80;
    display: flex; align-items: center; gap: 0.5rem;
    box-shadow: var(--shadow-lg);
  }

  /* Quran Text */
  @import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap');
`;

const ALARM_SOUND_URL = "https://assets.mixkit.co/active_storage/sfx/933/933-preview.mp3";

export default function HuzurVaktiPage2() {
  const [activeTab, setActiveTab] = useState('home');
  const [darkMode, setDarkMode] = useState(false);
  const [showToast, setShowToast] = useState({ show: false, msg: '' });
  
  const [favorites, setFavorites] = useState([]);
  const [showFavModal, setShowFavModal] = useState(false);

  // Vakitler & Alarm & Sehir
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [timeLeftStr, setTimeLeftStr] = useState('--:--:--');
  const [alarms, setAlarms] = useState({ Fajr: true, Sunrise: false, Dhuhr: true, Asr: true, Maghrib: true, Isha: true });
  const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);
  const [lastAlarmTime, setLastAlarmTime] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [showCityModal, setShowCityModal] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  
  // Kuran
  const [surahList, setSurahList] = useState([]);
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [quranData, setQuranData] = useState([]);
  const [activeAyahIndex, setActiveAyahIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Diğer
  const [selectedBook, setSelectedBook] = useState(null);
  const [dailyHadith, setDailyHadith] = useState(null);
  
  // Tesbih
  const [tesbihCount, setTesbihCount] = useState(0);
  const [activeTesbih, setActiveTesbih] = useState(PRESET_TESBIHAT[0]);
  const [inputModal, setInputModal] = useState({ isOpen: false, type: null, name: '', target: '' });

  // Qibla State
  const [qiblaAngle, setQiblaAngle] = useState(0);
  const [deviceHeading, setDeviceHeading] = useState(0);
  const [manualCompass, setManualCompass] = useState(false);

  const audioRef = useRef(null);
  const activeAyahRef = useRef(null);
  const scrollRef = useRef(null);
  const alarmAudioRef = useRef(null);

  // --- HELPER FOR QIBLA ---
  const calculateQibla = (lat, lng) => {
      const mLat = 21.4225, mLng = 39.8262;
      const y = Math.sin((mLng-lng) * Math.PI/180) * Math.cos(mLat * Math.PI/180);
      const x = Math.cos(lat*Math.PI/180)*Math.sin(mLat*Math.PI/180) - Math.sin(lat*Math.PI/180)*Math.cos(mLat*Math.PI/180)*Math.cos((mLng-lng)*Math.PI/180);
      return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  };

  // --- CALLBACKS ---
  const calculateNextPrayer = useCallback((timings) => {
      const now = new Date(); const curr = now.getHours() * 60 + now.getMinutes();
      const prayers = [ {n:'İmsak',t:timings.Fajr}, {n:'Güneş',t:timings.Sunrise}, {n:'Öğle',t:timings.Dhuhr}, {n:'İkindi',t:timings.Asr}, {n:'Akşam',t:timings.Maghrib}, {n:'Yatsı',t:timings.Isha} ];
      let next = null, minDiff = 9999;
      for(let p of prayers) {
          const [h, m] = p.t.split(':').map(Number);
          const diff = (h*60+m) - curr + ((h*60+m) - curr < 0 ? 1440 : 0);
          if(diff < minDiff) { minDiff = diff; next = p; }
      }
      setNextPrayer(next);
      const rh = Math.floor(minDiff / 60), rm = minDiff % 60, rs = 59 - now.getSeconds();
      setTimeLeftStr(`${rh}:${rm<10?'0'+rm:rm}:${rs<10?'0'+rs:rs}`);
  }, []);

  const fetchPrayerTimes = useCallback(async (lat, lng) => {
      try {
        const d = new Date(); const dateStr = `${d.getDate()}-${d.getMonth()+1}-${d.getFullYear()}`;
        const res = await fetch(`https://api.aladhan.com/v1/timings/${dateStr}?latitude=${lat}&longitude=${lng}&method=13`);
        const data = await res.json();
        setPrayerTimes(data.data.timings); calculateNextPrayer(data.data.timings);
      } catch (e) {}
  }, [calculateNextPrayer]);

  const getUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
           const { latitude, longitude } = pos.coords;
           setSelectedCity({name: "Konum", lat: latitude, lng: longitude});
           fetchPrayerTimes(latitude, longitude);
           setQiblaAngle(calculateQibla(latitude, longitude));
        },
        () => { 
            const istanbul = CITIES.find(c => c.name === "İstanbul");
            setSelectedCity(istanbul);
            fetchPrayerTimes(istanbul.lat, istanbul.lng);
            setQiblaAngle(calculateQibla(istanbul.lat, istanbul.lng));
        }
      );
    } else {
        const istanbul = CITIES.find(c => c.name === "İstanbul");
        setSelectedCity(istanbul);
        fetchPrayerTimes(istanbul.lat, istanbul.lng);
        setQiblaAngle(calculateQibla(istanbul.lat, istanbul.lng));
    }
  }, [fetchPrayerTimes]);

  const triggerAlarm = useCallback((prayerName) => {
      setIsAlarmPlaying(true);
      if (alarmAudioRef.current) { alarmAudioRef.current.currentTime = 0; alarmAudioRef.current.play().catch(e => console.log(e)); }
      const trNames = { Fajr: 'İmsak', Sunrise: 'Güneş', Dhuhr: 'Öğle', Asr: 'İkindi', Maghrib: 'Akşam', Isha: 'Yatsı' };
      showNotification(`${trNames[prayerName]} Vakti Geldi!`);
      if (navigator.vibrate) navigator.vibrate([500, 200, 500]);
  }, []);

  const checkAlarm = useCallback((times) => {
      const now = new Date();
      const currentTimeStr = `${now.getHours()}:${now.getMinutes() < 10 ? '0'+now.getMinutes() : now.getMinutes()}`;
      if (currentTimeStr === lastAlarmTime) return;
      const timeKeys = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
      for (const key of timeKeys) {
          const prayerTime = times[key].split(' ')[0];
          if (prayerTime === currentTimeStr && alarms[key]) {
              triggerAlarm(key); setLastAlarmTime(currentTimeStr); break;
          }
      }
  }, [alarms, lastAlarmTime, triggerAlarm]);

  const handleCitySelect = (city) => {
      setSelectedCity(city);
      localStorage.setItem('selectedCity', JSON.stringify(city));
      fetchPrayerTimes(city.lat, city.lng);
      setQiblaAngle(calculateQibla(city.lat, city.lng));
      setShowCityModal(false);
      showNotification(`${city.name} seçildi`);
  };

  const handleOrientation = useCallback((e) => {
      let heading = e.webkitCompassHeading || Math.abs(e.alpha - 360);
      setDeviceHeading(heading);
  }, []);

  const handleStartCompass = () => {
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission()
            .then(response => {
                if (response === 'granted') {
                    window.addEventListener('deviceorientation', handleOrientation);
                    setManualCompass(false);
                } else {
                    setManualCompass(true);
                }
            })
            .catch(() => setManualCompass(true));
    } else {
        window.addEventListener('deviceorientationabsolute', handleOrientation, true);
        setManualCompass(false);
    }
  };

  // --- EFFECTS ---
  useEffect(() => {
    // LocalStorage Load
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') setDarkMode(true);
    const savedFavs = localStorage.getItem('favorites');
    if (savedFavs) setFavorites(JSON.parse(savedFavs));
    const savedAlarms = localStorage.getItem('alarms');
    if (savedAlarms) setAlarms(JSON.parse(savedAlarms));
    
    // City Load
    const savedCity = localStorage.getItem('selectedCity');
    if (savedCity) {
      const city = JSON.parse(savedCity);
      setSelectedCity(city);
      fetchPrayerTimes(city.lat, city.lng);
      setQiblaAngle(calculateQibla(city.lat, city.lng));
    } else {
      getUserLocation();
    }

    prepareSurahList();

    const allHadiths = HADITH_LIBRARY.flatMap(book => book.hadiths.map(h => ({...h, source: book.title})));
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
    setDailyHadith(allHadiths[dayOfYear % allHadiths.length]);

    alarmAudioRef.current = new Audio(ALARM_SOUND_URL);
    alarmAudioRef.current.loop = true;
  }, [fetchPrayerTimes, getUserLocation]);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    if(prayerTimes) {
        const t = setInterval(() => { calculateNextPrayer(prayerTimes); checkAlarm(prayerTimes); }, 1000);
        return () => clearInterval(t);
    }
  }, [prayerTimes, alarms, lastAlarmTime, calculateNextPrayer, checkAlarm]);

  useEffect(() => {
    if (audioRef.current) {
        if (isPlaying) { audioRef.current.play().catch(e => { console.log(e); setIsPlaying(false); }); } 
        else { audioRef.current.pause(); }
    }
    if (isPlaying && activeAyahRef.current) {
        activeAyahRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isPlaying, activeAyahIndex]);

  useEffect(() => {
    if (scrollRef.current) {
        const selectedBtn = scrollRef.current.querySelector('.tesbih-item.active');
        if (selectedBtn) { selectedBtn.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); }
    }
  }, [activeTesbih]);

  // Qibla Compass Effect
  useEffect(() => {
    if (activeTab === 'qibla') {
      // iOS 13+ requires permission, show button
      if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
        setManualCompass(true);
      } else {
        // Android / Non-iOS auto start
        if ('ondeviceorientationabsolute' in window) {
             window.addEventListener('deviceorientationabsolute', handleOrientation, true);
        } else if ('ondeviceorientation' in window) {
             window.addEventListener('deviceorientation', handleOrientation, true);
        }
        setManualCompass(false);
      }
    }
    return () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [activeTab, handleOrientation]);

  // --- FUNCTIONS ---
  const showNotification = (msg) => { setShowToast({ show: true, msg }); setTimeout(() => setShowToast({ show: false, msg: '' }), 2000); };

  const openCreateModal = () => setInputModal({ isOpen: true, type: 'create', name: '', target: '100' });
  const openEditModal = () => setInputModal({ isOpen: true, type: 'edit', name: activeTesbih.label, target: activeTesbih.target });

  const handleSaveModal = () => {
      const targetVal = parseInt(inputModal.target);
      if (!targetVal || targetVal <= 0) { showNotification("Geçersiz sayı!"); return; }
      if (inputModal.type === 'create') {
          if (!inputModal.name) { showNotification("İsim giriniz!"); return; }
          const newDhikr = { id: Date.now(), label: inputModal.name, target: targetVal, meaning: "Özel Tesbihat" };
          setActiveTesbih(newDhikr); setTesbihCount(0); showNotification("Oluşturuldu!");
      } else if (inputModal.type === 'edit') {
          setActiveTesbih(prev => ({ ...prev, target: targetVal }));
          showNotification("Güncellendi!");
      }
      setInputModal({ ...inputModal, isOpen: false });
  };

  const handleTesbihClick = () => {
      const next = tesbihCount + 1;
      setTesbihCount(next);
      if (navigator.vibrate) navigator.vibrate(15);
      if (next % activeTesbih.target === 0) {
          if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
          showNotification(`${activeTesbih.label} tamamlandı!`);
      }
  };

  const stopAlarm = () => {
      setIsAlarmPlaying(false);
      if (alarmAudioRef.current) { alarmAudioRef.current.pause(); alarmAudioRef.current.currentTime = 0; }
  };

  const toggleAlarmSetting = (key) => {
      const newAlarms = { ...alarms, [key]: !alarms[key] };
      setAlarms(newAlarms); localStorage.setItem('alarms', JSON.stringify(newAlarms));
      showNotification(newAlarms[key] ? "Alarm Açıldı" : "Alarm Kapandı");
  };

  const toggleFavorite = (item) => {
      let newFavs;
      const itemId = item.type === 'hadith' ? item.id : `ayah-${selectedSurah.number}-${item.number}`;
      const favItem = item.type === 'hadith' ? { ...item, uniqueId: itemId } : { type: 'ayah', uniqueId: itemId, textTr: item.textTr, textAr: item.textAr, textLat: item.textLat, surahName: selectedSurah.name, number: item.number };
      const exists = favorites.find(f => f.uniqueId === itemId);
      if (exists) {
          newFavs = favorites.filter(f => f.uniqueId !== itemId);
          showNotification("Çıkarıldı");
      } else {
          newFavs = [favItem, ...favorites];
          showNotification("Eklendi");
      }
      setFavorites(newFavs); localStorage.setItem('favorites', JSON.stringify(newFavs));
  };

  const isFavorite = (item) => {
      const itemId = item.type === 'hadith' ? item.id : `ayah-${selectedSurah?.number}-${item.number}`;
      return favorites.some(f => f.uniqueId === itemId);
  };

  const prepareSurahList = async () => {
    try {
      const res = await fetch('https://api.alquran.cloud/v1/surah'); const data = await res.json();
      setSurahList(data.data.map((item, i) => ({ ...item, nameTR: SURAH_NAMES_TR[i] })));
    } catch { setSurahList(SURAH_NAMES_TR.map((n, i) => ({ number: i+1, nameTR: n, numberOfAyahs: 0 }))); }
  };
  const openSurah = async (number, nameTR) => {
      setLoading(true);
      try {
          const audioReq = fetch(`https://api.alquran.cloud/v1/surah/${number}/editions/ar.alafasy`);
          const textReq = fetch(`https://api.acikkuran.com/surah/${number}`);
          const [audioRes, textRes] = await Promise.all([audioReq, textReq]);
          const audioData = await audioRes.json(); const textData = await textRes.json();
          const verses = textData.data.verses.map((verse, index) => {
              const audioUrl = audioData.data && audioData.data[0].ayahs[index] ? audioData.data[0].ayahs[index].audio : null;
              return { number: verse.verse_number, textAr: verse.verse, textTr: verse.translation.text, textLat: verse.transcription, audio: audioUrl };
          });
          setQuranData(verses); setSelectedSurah({ number, name: nameTR }); setActiveAyahIndex(0); setIsPlaying(false); setActiveTab('read');
      } catch (err) { showNotification("Veriler alınamadı."); }
      setLoading(false);
  };

  // --- RENDER ---

  return (
    <div className="app-container">
      <style>{cssStyles}</style>
      
      {/* Header */}
      <header className="header">
          <div className="flex-row" onClick={() => setShowCityModal(true)}>
             <div className="header-logo" style={{fontSize:'0.7rem', display:'flex', flexDirection:'column', gap:0, lineHeight:1}}>
                 <MapPin size={12}/>
             </div>
             <div style={{display:'flex', flexDirection:'column', lineHeight:1.2}}>
                 <span className="font-bold text-sm">Huzur Vakti</span>
                 <span className="text-xs text-muted" style={{maxWidth:'100px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'var(--text-muted)'}}>{selectedCity ? selectedCity.name : "Konum"}</span>
             </div>
          </div>
          <div className="flex-row">
             {selectedSurah && (
                 <div className="flex-row" style={{background: 'var(--primary-light)', padding: '0.25rem 0.75rem', borderRadius: '99px'}}>
                     <span className="text-xs font-bold" style={{color:'var(--primary-dark)'}}>{selectedSurah.name} {activeAyahIndex + 1}</span>
                     <button onClick={() => setIsPlaying(!isPlaying)} className="btn-icon" style={{background: 'var(--primary)', color:'white', width:'1.5rem', height:'1.5rem', padding:0}}>
                        {isPlaying ? <Pause size={12}/> : <Play size={12}/>}
                     </button>
                 </div>
             )}
             <button onClick={() => setShowFavModal(true)} className="btn-icon"><Heart size={20}/></button>
             <button onClick={() => setDarkMode(!darkMode)} className="btn-icon">{darkMode ? <Sun size={20}/> : <Moon size={20}/>}</button>
          </div>
      </header>

      {/* City Selection Modal */}
      {showCityModal && (
          <div className="modal-overlay">
              <div className="modal-content" style={{maxHeight:'80vh'}}>
                  <div className="flex-between" style={{marginBottom:'1rem'}}>
                      <h3 className="text-lg font-bold">Şehir Seçin</h3>
                      <button onClick={() => setShowCityModal(false)} className="btn-icon"><X size={20}/></button>
                  </div>
                  <input type="text" className="input-field" placeholder="Şehir Ara..." value={citySearch} onChange={(e) => setCitySearch(e.target.value)} />
                  <div style={{overflowY:'auto', flex:1}}>
                      <div className="card" onClick={getUserLocation} style={{cursor:'pointer', background:'var(--primary-light)', color:'var(--primary-dark)', fontWeight:'bold', display:'flex', alignItems:'center', gap:'0.5rem'}}>
                          <Compass size={18}/> Konumumu Bul
                      </div>
                      {CITIES.filter(c => c.name.toLowerCase().includes(citySearch.toLowerCase())).map(city => (
                          <div key={city.name} onClick={() => handleCitySelect(city)} style={{padding:'0.75rem', borderBottom:'1px solid var(--border)', cursor:'pointer'}}>
                              {city.name}
                          </div>
                      ))}
                  </div>
              </div>
          </div>
      )}

      {/* Alarm Modal */}
      {isAlarmPlaying && (
          <div className="modal-overlay">
              <div className="modal-content text-center">
                  <div className="flex-center" style={{margin:'0 auto 1rem auto', width:'4rem', height:'4rem', borderRadius:'50%', background:'var(--primary-light)'}}>
                      <Volume2 size={32} color="var(--primary)" />
                  </div>
                  <h2 className="text-2xl font-bold">Vakit Geldi!</h2>
                  <p className="text-muted" style={{margin:'0.5rem 0 1.5rem 0'}}>Namaz vakti girdi.</p>
                  <button onClick={stopAlarm} className="btn-primary">Alarmı Durdur</button>
              </div>
          </div>
      )}

      {/* Input Modal */}
      {inputModal.isOpen && (
          <div className="modal-overlay">
              <div className="modal-content">
                  <h3 className="text-lg font-bold" style={{marginBottom:'1rem'}}>{inputModal.type === 'create' ? 'Özel Tesbih' : 'Hedef Düzenle'}</h3>
                  {inputModal.type === 'create' && <input type="text" className="input-field" placeholder="Zikir Adı" value={inputModal.name} onChange={(e) => setInputModal({...inputModal, name: e.target.value})} />}
                  <input type="number" className="input-field" placeholder="Hedef" value={inputModal.target} onChange={(e) => setInputModal({...inputModal, target: e.target.value})} />
                  <div className="grid-2">
                      <button onClick={() => setInputModal({...inputModal, isOpen: false})} className="btn-icon" style={{width:'100%', borderRadius:'0.5rem', border:'1px solid var(--border)'}}>İptal</button>
                      <button onClick={handleSaveModal} className="btn-primary" style={{borderRadius:'0.5rem', padding:'0.75rem'}}>Kaydet</button>
                  </div>
              </div>
          </div>
      )}

      {/* Favorites Modal */}
      {showFavModal && (
          <div className="modal-overlay" onClick={(e) => {if(e.target === e.currentTarget) setShowFavModal(false)}}>
              <div className="modal-content" style={{maxHeight:'80vh', display:'flex', flexDirection:'column'}}>
                   <div className="flex-between" style={{marginBottom:'1rem'}}>
                       <h2 className="text-lg font-bold">Favorilerim</h2>
                       <button onClick={() => setShowFavModal(false)} className="btn-icon"><X size={20}/></button>
                   </div>
                   <div style={{overflowY:'auto', flex:1}}>
                       {favorites.length === 0 ? <p className="text-center text-muted">Henüz favori yok.</p> : favorites.map((fav, i) => (
                           <div key={i} className="card">
                               <div className="flex-between">
                                   <span className="text-xs font-bold text-primary">{fav.type === 'hadith' ? 'Hadis' : `${fav.surahName} ${fav.number}`}</span>
                                   <button onClick={() => toggleFavorite(fav)} style={{color:'var(--danger)', background:'none', border:'none'}}><Trash2 size={16}/></button>
                               </div>
                               <p className="text-sm" style={{marginTop:'0.5rem'}}>"{fav.textTr || fav.text}"</p>
                           </div>
                       ))}
                   </div>
              </div>
          </div>
      )}

      {/* MAIN CONTENT */}
      <main>
          
          {/* HOME */}
          {activeTab === 'home' && (
              <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                  
                  {/* YENİ KONUM KARTI (Buraya Eklendi) */}
                  <div 
                    onClick={() => setShowCityModal(true)}
                    className="card flex-between" 
                    style={{cursor:'pointer', padding:'1rem', borderLeft:'4px solid var(--primary)'}}
                  >
                      <div className="flex-row">
                          <div style={{background:'var(--primary-light)', padding:'0.5rem', borderRadius:'50%', color:'var(--primary-dark)'}}>
                              <MapPin size={20} />
                          </div>
                          <div>
                              <p className="text-xs font-bold text-muted uppercase">KONUM</p>
                              <h3 className="font-bold text-lg">{selectedCity ? selectedCity.name : "Şehir Seçiniz"}</h3>
                          </div>
                      </div>
                      <span className="text-xs font-bold text-primary" style={{background:'var(--bg-body)', padding:'0.25rem 0.5rem', borderRadius:'0.5rem'}}>DEĞİŞTİR</span>
                  </div>

                  <div className="vakit-card">
                      <div>
                          <p className="text-xs font-bold" style={{opacity:0.8}}>SONRAKİ VAKİT</p>
                          <h2 className="text-2xl font-bold">{nextPrayer?.n || '...'}</h2>
                      </div>
                      <div style={{background:'rgba(255,255,255,0.1)', padding:'0.5rem 1rem', borderRadius:'0.75rem'}}>
                          <span className="text-2xl font-bold" style={{fontFamily:'monospace'}}>{timeLeftStr}</span>
                      </div>
                  </div>
                  
                  {prayerTimes && (
                      <div className="grid-3">
                          {['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'].map((key, i) => { 
                              const names = ['İmsak', 'Güneş', 'Öğle', 'İkindi', 'Akşam', 'Yatsı'];
                              const isNext = nextPrayer?.n === names[i]; 
                              return (
                                  <div key={key} className="card flex-col flex-center" style={{borderColor: isNext ? 'var(--primary)' : '', padding:'0.75rem'}}>
                                      <p className="text-xs font-bold text-muted uppercase">{names[i]}</p>
                                      <p className="font-bold text-lg">{prayerTimes[key].split(' ')[0]}</p>
                                      <button onClick={() => toggleAlarmSetting(key)} className="btn-icon" style={{padding: '0.25rem', marginTop:'0.25rem', color: alarms[key] ? 'var(--primary)' : ''}}>{alarms[key] ? <Bell size={14}/> : <BellOff size={14}/>}</button>
                                  </div>
                              )
                          })}
                      </div>
                  )}

                  {dailyHadith && (
                      <div className="card" style={{borderLeft:'4px solid var(--primary)'}}>
                          <div className="flex-row" style={{marginBottom:'0.5rem'}}><ScrollText size={16} color="var(--primary)"/><span className="text-xs font-bold text-muted">GÜNÜN HADİSİ</span></div>
                          <p className="text-lg" style={{fontStyle:'italic', fontFamily:'var(--font-serif)'}}>"{dailyHadith.text}"</p>
                          <div className="flex-between" style={{marginTop:'1rem'}}>
                              <span className="text-xs font-bold text-primary">{dailyHadith.source}</span>
                              <div className="flex-row">
                                  <button onClick={() => toggleFavorite({...dailyHadith, type: 'hadith'})} className="btn-icon"><Heart size={18} fill={isFavorite({...dailyHadith, type:'hadith'}) ? 'currentColor' : 'none'}/></button>
                                  <button onClick={() => navigator.clipboard.writeText(dailyHadith.text)} className="btn-icon"><Share2 size={18}/></button>
                              </div>
                          </div>
                      </div>
                  )}
              </div>
          )}

          {/* QURAN */}
          {activeTab === 'quran' && (
              <div>
                  <div style={{position:'sticky', top:'4rem', background:'var(--bg-body)', padding:'0.5rem 0', zIndex:10}}>
                      <div style={{position:'relative'}}>
                          <Search size={18} style={{position:'absolute', left:'1rem', top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)'}}/>
                          <input type="text" className="input-field" style={{paddingLeft:'2.5rem', marginBottom:0}} placeholder="Sure Ara..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                      </div>
                  </div>
                  {loading ? <div className="text-center" style={{padding:'2rem'}}>Yükleniyor...</div> : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.75rem'}}>
                          {surahList.filter(s => s.nameTR.toLowerCase().includes(searchQuery.toLowerCase())).map(s => (
                              <div key={s.number} onClick={() => openSurah(s.number, s.nameTR)} className="card flex-row" style={{cursor:'pointer'}}>
                                  <div style={{width:'2.5rem', height:'2.5rem', background:'var(--bg-body)', borderRadius:'0.5rem', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'bold', color:'var(--primary)'}}>{s.number}</div>
                                  <div style={{flex:1}}><h3 className="font-bold">{s.nameTR}</h3><p className="text-xs text-muted">{s.numberOfAyahs} Ayet</p></div>
                                  <ChevronRight size={20} className="text-muted"/>
                              </div>
                          ))}
                      </div>
                  )}
              </div>
          )}

          {/* READ MODE */}
          {activeTab === 'read' && selectedSurah && (
              <div>
                   <div className="flex-between" style={{marginBottom:'1rem'}}>
                        <button onClick={() => {setActiveTab('quran'); setIsPlaying(false)}} className="btn-icon" style={{width:'auto', paddingRight:'1rem'}}><ArrowLeft size={18} style={{marginRight:'0.5rem'}}/> Geri</button>
                        <h2 className="text-lg font-bold">{selectedSurah.name}</h2>
                        <div style={{width:'4rem'}}></div>
                   </div>
                   <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
                        {quranData.map((ayah, idx) => (
                            <div key={idx} ref={idx === activeAyahIndex ? activeAyahRef : null} className="card" style={{borderColor: idx === activeAyahIndex ? 'var(--primary)' : ''}}>
                                <div className="flex-between" style={{alignItems:'flex-start', marginBottom:'0.5rem'}}>
                                    <span style={{background:'var(--bg-body)', padding:'0.25rem 0.5rem', borderRadius:'0.25rem', fontSize:'0.75rem', fontWeight:'bold', color:'var(--text-muted)'}}>{ayah.number}</span>
                                    <p style={{fontFamily:'var(--font-serif)', fontSize:'1.5rem', textAlign:'right', width:'100%'}} dir="rtl">{ayah.textAr}</p>
                                </div>
                                <p className="text-xs text-muted" style={{fontStyle:'italic', marginBottom:'0.5rem'}}>"{ayah.textLat}"</p>
                                <p className="text-sm">{ayah.textTr}</p>
                                <div className="flex-row" style={{justifyContent:'flex-end', marginTop:'0.5rem'}}>
                                    <button onClick={() => toggleFavorite(ayah)} className="btn-icon"><Heart size={18} fill={isFavorite(ayah) ? 'var(--danger)' : 'none'} color={isFavorite(ayah) ? 'var(--danger)' : 'currentColor'}/></button>
                                    <button 
                                        onClick={() => {
                                            if (activeAyahIndex === idx) {
                                                setIsPlaying(!isPlaying);
                                            } else {
                                                setActiveAyahIndex(idx);
                                                setIsPlaying(true);
                                            }
                                        }} 
                                        className="btn-icon" 
                                        style={{color: idx === activeAyahIndex && isPlaying ? 'var(--primary)' : ''}}
                                    >
                                        {idx === activeAyahIndex && isPlaying ? <Pause size={18}/> : <Play size={18}/>}
                                    </button>
                                </div>
                            </div>
                        ))}
                   </div>
              </div>
          )}

          {/* HADITH */}
          {activeTab === 'hadith' && (
              <div>
                  {!selectedBook ? (
                      <div className="grid-2">
                          {HADITH_LIBRARY.map(book => (
                              <div key={book.id} onClick={() => setSelectedBook(book)} className="card" style={{background: book.color, color: book.textColor, border:'none', cursor:'pointer', height:'10rem', display:'flex', flexDirection:'column', justifyContent:'space-between'}}>
                                  <h3 className="text-lg font-bold">{book.title}</h3>
                                  <p className="text-sm" style={{opacity:0.8}}>{book.author}</p>
                              </div>
                          ))}
                      </div>
                  ) : (
                      <div>
                          <button onClick={() => setSelectedBook(null)} className="btn-icon" style={{width:'auto', marginBottom:'1rem'}}><ArrowLeft size={18} style={{marginRight:'0.5rem'}}/> Geri</button>
                          <h2 className="text-xl font-bold" style={{marginBottom:'1rem'}}>{selectedBook.title}</h2>
                          <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
                              {selectedBook.hadiths.map(h => (
                                  <div key={h.id} className="card">
                                      <div className="flex-between" style={{marginBottom:'0.5rem'}}>
                                          <span style={{fontSize:'0.7rem', fontWeight:'bold', background:'var(--bg-body)', padding:'0.25rem 0.5rem', borderRadius:'0.25rem'}}>{h.topic}</span>
                                          <button onClick={() => toggleFavorite({...h, type:'hadith'})} className="btn-icon"><Heart size={18} fill={isFavorite({...h, type:'hadith'}) ? 'var(--danger)' : 'none'} color={isFavorite({...h, type:'hadith'}) ? 'var(--danger)' : 'currentColor'}/></button>
                                      </div>
                                      <p className="text-lg" style={{fontFamily:'var(--font-serif)'}}>"{h.text}"</p>
                                  </div>
                              ))}
                          </div>
                      </div>
                  )}
              </div>
          )}

          {/* TESBIH */}
          {activeTab === 'tesbih' && (
              <div style={{height:'calc(100vh - 12rem)', display:'flex', flexDirection:'column'}}>
                  
                  {/* Zikir Listesi */}
                  <div style={{marginBottom:'2rem'}}>
                      <div className="tesbih-scroll" ref={scrollRef}>
                          <button onClick={openCreateModal} className="tesbih-item" style={{borderStyle:'dashed', display:'flex', alignItems:'center', gap:'0.25rem'}}><Plus size={16}/> Özel</button>
                          {PRESET_TESBIHAT.map(t => (
                              <button key={t.id} onClick={() => {setActiveTesbih(t); setTesbihCount(0)}} className={`tesbih-item ${activeTesbih.id === t.id ? 'active' : ''}`}>{t.label} <span style={{opacity:0.7, fontSize:'0.7rem'}}>({t.target})</span></button>
                          ))}
                      </div>
                  </div>

                  {/* Info */}
                  <div className="text-center" style={{marginBottom:'2rem'}}>
                      <h2 className="text-2xl font-bold">{activeTesbih.label}</h2>
                      <p className="text-xs text-muted">{activeTesbih.meaning}</p>
                      <div className="flex-center" style={{gap:'1rem', marginTop:'1rem'}}>
                          <div style={{background:'var(--bg-card)', border:'1px solid var(--border)', padding:'0.25rem 0.75rem', borderRadius:'99px', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:'0.5rem'}}>
                              Hedef: {activeTesbih.target}
                              <button onClick={openEditModal} style={{background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)'}}><Edit size={14}/></button>
                          </div>
                          <button onClick={() => {setTesbihCount(0); showNotification("Sıfırlandı")}} className="btn-icon"><RotateCcw size={18}/></button>
                      </div>
                  </div>

                  {/* Button */}
                  <div className="tesbih-ring-container">
                      <svg style={{position:'absolute', inset:0, width:'100%', height:'100%', transform:'rotate(-90deg)', pointerEvents:'none'}} viewBox="0 0 100 100">
                          {/* İmame */}
                          <g transform="translate(50, 50)">
                              <path d="M46,-45 L50,-52 L54,-45" fill="var(--primary)" />
                              <circle cx="50" cy="-42" r="2" fill="var(--primary)" />
                          </g>
                          {Array.from({length: 33}).map((_, i) => {
                              const angle = (i / 33) * 360;
                              const x = 50 + 42 * Math.cos((angle * Math.PI) / 180);
                              const y = 50 + 42 * Math.sin((angle * Math.PI) / 180);
                              const currentPos = tesbihCount % 33;
                              const fillLimit = (currentPos === 0 && tesbihCount > 0) ? 33 : currentPos;
                              const isFilled = i < fillLimit;
                              return <circle key={i} cx={x} cy={y} r={isFilled ? 2.5 : 1.5} fill={isFilled ? 'var(--primary)' : 'var(--border)'} style={{transition:'fill 0.3s'}} />;
                          })}
                      </svg>
                      <button className="tesbih-btn" onClick={handleTesbihClick}>
                          <span className="text-4xl font-bold">{tesbihCount}</span>
                          <span className="text-xs font-bold text-primary uppercase">ÇEK</span>
                      </button>
                  </div>
              </div>
          )}

          {/* QIBLA */}
          {activeTab === 'qibla' && (
               <div style={{height:'calc(100vh - 12rem)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
                   {manualCompass && (
                       <button onClick={handleStartCompass} className="btn-primary" style={{marginBottom: '2rem'}}>Pusulayı Başlat (İzin Gerekli)</button>
                   )}
                   <div style={{width:'250px', height:'250px', borderRadius:'50%', border:'4px solid var(--border)', position:'relative', background:'var(--bg-card)', boxShadow:'var(--shadow-lg)', display:'flex', alignItems:'center', justifyContent:'center', transform: `rotate(${-deviceHeading}deg)`, transition: 'transform 0.2s'}}>
                       <div style={{position:'absolute', top:'10px', fontWeight:'bold', color:'var(--danger)'}}>K</div>
                       <div style={{position:'absolute', inset:0, display:'flex', justifyContent:'center', transform:`rotate(${qiblaAngle}deg)`, transition:'transform 0.5s'}}>
                            <div style={{width:'4px', height:'100px', background:'var(--primary)', borderRadius:'2px', marginTop:'20px'}}></div>
                       </div>
                   </div>
                   <div className="text-center" style={{marginTop:'2rem'}}>
                       <p className="text-4xl font-bold text-primary">{Math.round(qiblaAngle)}°</p>
                       <p className="text-sm text-muted uppercase">Kıble Açısı</p>
                   </div>
               </div>
          )}

      </main>

      <audio ref={audioRef} src={quranData[activeAyahIndex]?.audio} onEnded={() => {if (activeAyahIndex < quranData.length - 1) setActiveAyahIndex(p => p + 1); else setIsPlaying(false);}} />
      <audio ref={alarmAudioRef} />

      {showToast.show && <div className="toast"><CheckCircle2 size={16} color="var(--primary-light)"/> {showToast.msg}</div>}

      <nav className="bottom-nav">
          {[
              { id: 'home', icon: Home, label: 'Ana Sayfa' },
              { id: 'quran', icon: BookOpen, label: 'Kuran' },
              { id: 'hadith', icon: Book, label: 'Hadis' },
              { id: 'tesbih', icon: RotateCw, label: 'Tesbih' },
              { id: 'qibla', icon: Compass, label: 'Kıble' },
          ].map(item => (
              <button key={item.id} onClick={() => setActiveTab(item.id)} className={`nav-item ${activeTab === item.id ? 'active' : ''}`}>
                  <item.icon size={24} />
                  <span>{item.label}</span>
              </button>
          ))}
      </nav>
    </div>
  );
}